import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'lib/utils'
import * as React from 'react'

/**
 * T3 Button.
 *
 * Canonical API (use this for new code):
 *   <Button variant="t3-primary | t3-tertiary | t3-ghost | t3-destructive"
 *           size="t3-sm | t3-md | t3-lg" />
 *
 * Spec: playbook/design-system/components.md#button-matrix.
 *
 * Hierarchy (May 2026 v3 — collapsed to 4 levels):
 *   - primary    → THE single hero action on a view. Rule of one.
 *   - tertiary   → anything important enough to be a button but not THE hero.
 *                  Includes paired confirm/cancel partners, settings entry
 *                  points, secondary CTAs. If two actions sit side-by-side
 *                  at similar importance, BOTH should be tertiary — don't
 *                  elevate one to primary just because there are two.
 *   - ghost      → inline utility, menu items, small icon buttons.
 *   - destructive → danger only (Delete, Discard, Remove).
 *
 * `t3-secondary` (the old violet recipe) was removed; it now aliases onto
 * tertiary so existing callsites render correctly. New code should not use
 * `t3-secondary` / `secondary`.
 *
 * Legacy variants (`acid` / `acid-sm` / `paper-sm` / `subtle-sm` /
 * `outline-subtle` / `ghost-dark` / `outline` / `secondary` / `ghost` /
 * `destructive` / `link` / `transparent` / `glassy`) are kept as ALIASES
 * onto the locked T3 matrix so the ~100 callsites that still reference
 * them render the new system pixel-correct without per-file edits. Each
 * alias notes its target. Once consumers are renamed (mechanical search/
 * replace), the alias rows can be deleted.
 *
 * `default` is an alias for `t3-primary` so any unspecified `<Button>` call
 * renders the canonical acid CTA.
 */

// Shared recipes so legacy aliases and the canonical t3-* variants stay
// in lockstep — change once, both sides update.
const T3 = {
  primary:
    'bg-acid text-ink-on-acid font-semibold tracking-[-0.005em] shadow-[var(--shadow-acid-cta)] hover:shadow-[var(--shadow-acid-cta-hover)] hover:-translate-y-px transition-all disabled:opacity-40 disabled:translate-y-0 disabled:shadow-none',
  tertiary:
    'bg-[var(--btn-tertiary-bg)] border border-line-bright text-paper font-medium ' +
    'shadow-[var(--btn-tertiary-shadow)] ' +
    'hover:bg-[var(--btn-tertiary-bg-hover)] hover:border-[color:var(--btn-tertiary-border-hover)] ' +
    'hover:translate-y-[var(--btn-tertiary-lift-hover)] ' +
    'hover:shadow-[var(--btn-tertiary-shadow-hover)] ' +
    'transition-all disabled:opacity-40 disabled:translate-y-0 disabled:shadow-none',
  ghost:
    'bg-transparent text-paper-dim hover:text-paper hover:bg-bg-3 font-medium transition-colors disabled:opacity-40',
  destructive:
    'bg-transparent border border-destructive text-destructive font-medium hover:bg-destructive-soft transition-colors disabled:opacity-40',
} as const

// Locked T3 button radius — sharp/architectural. Picked May 2026 v2 to
// give product chrome a more structured, magazine-y feel distinct from
// the friendlier 9-12px curves of card surfaces. Single source of truth;
// every variant below composes through ${T3_RADIUS}.
const T3_RADIUS = 'rounded-[6px]'

const buttonVariants = cva(
  `inline-flex items-center justify-center gap-2 whitespace-nowrap ${T3_RADIUS} shrink-0 cursor-pointer text-sm font-medium transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4`,
  {
    variants: {
      variant: {
        // ---- T3 BUTTON MATRIX (locked, canonical) ----
        // Hierarchy: primary → tertiary → ghost → destructive.
        // Rule of one: at most one primary per view. Two equally-important
        // actions side-by-side should BOTH be tertiary.
        // `t3-secondary` aliases onto tertiary for back-compat.
        't3-primary': `${T3_RADIUS} ${T3.primary}`,
        't3-secondary': `${T3_RADIUS} ${T3.tertiary}`,
        't3-tertiary': `${T3_RADIUS} ${T3.tertiary}`,
        't3-ghost': `${T3_RADIUS} ${T3.ghost}`,
        't3-destructive': `${T3_RADIUS} ${T3.destructive}`,

        // ---- Aliases for back-compat with existing callsites ----
        // Each row maps to one of the five T3 levels above. Pixels match
        // the canonical recipe so a callsite using `variant="t3-primary" size="t3-md"` and
        // one using `variant="t3-primary"` render identically.

        // Unspecified `<Button>` → t3-primary (acid CTA).
        default: `${T3_RADIUS} ${T3.primary}`,

        // Older T3-era hero CTA. Was 48px tall + text-[14px] — keeping the
        // baked-in height because callsites don't pass `size`. Equivalent
        // to `t3-primary` + `t3-lg`-ish.
        acid: `!h-[48px] ${T3_RADIUS} text-[14px] ${T3.primary}`,
        // Older T3-era inline acid CTA — equivalent to `t3-primary` +
        // `t3-sm`.
        'acid-sm': `h-8 px-3 ${T3_RADIUS} text-[13px] ${T3.primary}`,

        // Older T3-era inverted-paper CTA used for "primary inside a
        // bright card". Paper-on-bg recipe — kept distinct because it
        // reads as "primary alternate for bright surfaces" rather than
        // either acid or tertiary. Token-only; no legacy purple.
        'paper-sm': `h-8 px-3 ${T3_RADIUS} bg-paper text-[13px] font-semibold tracking-[-0.005em] text-bg transition-all hover:-translate-y-px hover:opacity-90 disabled:translate-y-0 disabled:opacity-40`,

        // Older T3-era subtle CTA (dark fill + border). Tertiary recipe
        // sized to match the old 32px height.
        'subtle-sm': `h-8 px-3 ${T3_RADIUS} text-[13px] ${T3.tertiary}`,

        // Older T3-era subtle bordered button. Tertiary recipe.
        'outline-subtle': `${T3_RADIUS} ${T3.tertiary}`,

        // Older T3-era ghost button with a 1px border. Tertiary recipe so
        // it stays visible — true text-only ghost lives at `t3-ghost`.
        'ghost-dark': `${T3_RADIUS} text-[13.5px] ${T3.tertiary}`,

        // Generic shadcn names — point at the matrix so any legacy
        // consumer renders the new system. `outline`/`secondary`/`ghost`/
        // `destructive` were the original shadcn variants on white-canvas
        // pages; on T3 they map to tertiary/secondary/ghost/destructive
        // respectively.
        outline: `${T3_RADIUS} ${T3.tertiary}`,
        secondary: `${T3_RADIUS} ${T3.tertiary}`,
        ghost: `${T3_RADIUS} ${T3.ghost}`,
        destructive: `${T3_RADIUS} ${T3.destructive}`,

        // Inline link-button. Kept distinct because it's not a "button"
        // visually — it's an underlined acid-text affordance.
        link: 'text-center text-[13px] font-normal text-acid hover:underline focus:underline focus:outline-none',

        // Decorative variants used for media chrome (image/full-view
        // overlays). These intentionally sit OUTSIDE the action matrix
        // because they're not "buttons" semantically — they're touch
        // targets on top of imagery.
        transparent: 'rounded-full border-none bg-bg-3/60 hover:bg-bg-3',
        glassy:
          'rounded-full bg-paper/20 text-paper shadow-lg backdrop-blur-sm transition-all duration-200 hover:border-paper/40 hover:bg-paper/30 disabled:bg-paper/10 disabled:text-paper/50',
      },
      size: {
        // Locked T3 sizes. Pair with t3-* variants above.
        // sm = inline / table rows, md = standard, lg = hero CTA.
        't3-sm': 'h-8 gap-1.5 px-3 text-[13px] has-[>svg]:pl-2.5',
        't3-md': 'h-10 gap-2 px-4 text-[14px] has-[>svg]:pl-3.5',
        't3-lg': 'h-12 gap-2 px-5 text-[15px] has-[>svg]:pl-4',

        // Legacy sizes kept for back-compat. Map onto roughly equivalent
        // T3 dimensions; consumers should migrate to `t3-sm|md|lg` as they
        // touch each callsite.
        default: 'h-10 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-12 px-5 has-[>svg]:px-4',
        cta: 'h-12 w-full',
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
